import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { workspaceService } from '@/services'
import { Workspace } from '@/types'
import type { WorkspacesFiltersState } from '@/components/workspaces'
import type { WorkspaceGroupByOption } from '@/lib/config/node-grouping-configs'
import { useAuth } from '@/hooks/useAuth'
import { NodeListPage } from '@/components/nodes/NodeListPage'
import { workspacesListConfig } from '@/lib/config/node-list-configs'

export default function WorkspacesPage() {
  const { user } = useAuth()
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<WorkspaceGroupByOption>('none')
  const [filters, setFilters] = useState<WorkspacesFiltersState>(
    workspacesListConfig.defaultFilters
  )

  // Handler for partial filter updates
  const handleFiltersChange = (partialFilters: Partial<WorkspacesFiltersState>) => {
    setFilters(
      (prev: WorkspacesFiltersState) => ({ ...prev, ...partialFilters }) as WorkspacesFiltersState
    )
  }

  const loadWorkspaces = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      const data = await workspaceService.getWorkspacesForUser(user.id)
      setWorkspaces(data)
    } catch {
      toast.error('Failed to load workspaces')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadWorkspaces()
  }, [loadWorkspaces])

  return (
    <NodeListPage
      nodes={workspaces}
      loading={loading}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={{
        ...workspacesListConfig,
        filterNodes: (
          nodes: Workspace[],
          tab: string | undefined,
          filterState: WorkspacesFiltersState,
          query: string
        ) =>
          workspacesListConfig.filterNodes(
            nodes,
            tab,
            filterState,
            query,
            user ? { id: user.id, role: user.role } : undefined
          ),
      }}
    />
  )
}
